<template>
	<div v-if="entities" class="grid-flex-container">
		<div class="grid-toolbar mb-4">
			<v-tabs @change="onTabItemChanged">
				<v-tab v-for="(entity, index) in entities" :key="index">{{
					entity.Name
				}}</v-tab>
			</v-tabs>
		</div>
		<v-container fluid class="px-4 mb-4">
			<v-row>
				<div style="flex-grow: 1">
					<v-text-field
						v-model="search"
						class="py-0 my-0 mb-2 mr-2"
						append-icon="mdi-magnify"
						label="Search"
						single-line
						hide-details
						clearable
					/>
				</div>
				<div>
					<v-btn v-if="canAdd" color="primary" @click.stop="addField">
						<v-icon>mdi-plus</v-icon>
						Add
					</v-btn>
				</div>
			</v-row>
		</v-container>
		<div class="generic-grid-container">
			<v-data-table
				:headers="headers"
				:items="items"
				:disable-pagination="true"
				:search="search"
				item-key="Id"
				sort-by="Order"
				fixed-header
				dense
				:loading="loading"
				hide-default-footer
				loading-text="Loading..."
			>
				<template v-slot:body="props">
					<draggable
						@change="reorder(props.items)"
						:list="props.items"
						tag="tbody"
					>
						<tr v-for="(item, index) in props.items" :key="index">
							<td>
								<v-icon small color="primary"
									>mdi-reorder-horizontal</v-icon
								>
							</td>
							<td>{{ item.Label }}</td>
							<td>
								[{{ item.DataField.EntityName }}].{{
									item.DataField.FieldName
								}}
							</td>
							<td>
								<v-icon color="primary" v-if="item.IsVisible"
									>mdi-check-circle</v-icon
								>
								<v-icon color="error" v-else
									>mdi-close-circle</v-icon
								>
							</td>
							<td>
								<v-icon
									class="mr-2"
									color="primary"
									v-if="item.LinkFieldId"
									>mdi-link-variant</v-icon
								>
								<span v-if="item.LinkFieldId && item.LinkField"
									>[{{ item.LinkField.EntityName }}].{{
										item.LinkField.FieldName
									}}</span
								>
							</td>
							<td>
								<v-icon
									class="mr-2"
									color="primary"
									v-if="item.ReferenceListTypeId"
									>mdi-form-dropdown</v-icon
								>
								<span v-if="item.ReferenceListTypeId"
									>{{ item.ReferenceListType }} - [{{
										item.DataField.EntityName
									}}].{{ item.DataFieldName }}</span
								>
							</td>
							<td>
								<v-btn
									v-if="canDelete"
									fab
									small
									icon
									elevation="1"
									class="action-btn float-right"
									@click.stop="onDeleteGridFields(item)"
								>
									<v-icon color="error" small
										>mdi-delete</v-icon
									>
								</v-btn>
								<v-btn
									v-if="canEdit"
									fab
									small
									icon
									elevation="1"
									class="action-btn float-right"
									@click.stop="onEditGridViewField(item)"
								>
									<v-icon color="primary" small
										>mdi-pencil</v-icon
									>
								</v-btn>
							</td>
						</tr>
					</draggable>
				</template>
			</v-data-table>
		</div>
		<grid-fields-modal
			v-if="showGridFieldsModal"
			:showDialog="showGridFieldsModal"
			@close="onCloseGridFieldsModal"
		/>
	</div>
</template>

<script>
import GridFieldsModal from './GridFieldsModal.vue';
import ConfirmDialog from '@/components/core/dialogs/confirmDialog';
import { mapGetters } from 'vuex';
import {
	GridsGetters,
	GridFieldsSettingsGetters,
} from '../../../store/getters';
import {
	GridFieldsSettingsActions,
	GridsActions,
} from '../../../store/actions';
import permissionsMixin from '@/mixins/permissionsMixin';
import { Permissions } from '../../../enums/permissionsEnum';
import Draggable from 'vuedraggable';
export default {
	name: 'GridFields',
	components: {
		GridFieldsModal,
		Draggable,
	},
	mixins: [permissionsMixin],
	data() {
		return {
			selectedEntity: null,
			reordered: [],
			showReorderModal: false,
			showGridFieldsModal: false,
			search: '',
			headers: [
				{ text: '', sortable: false, value: 'Draggable', width: 10 },
				{ text: 'Label', sortable: false, value: 'Label' },
				{ text: 'Field Name', sortable: false, value: 'DataFieldName' },
				{ text: 'Visible', sortable: false, value: 'IsVisible' },
				{ text: 'Link Field', sortable: false, value: 'LinkFieldId' },
				{
					text: 'Reference Field',
					sortable: false,
					value: 'ReferenceListTypeId',
				},
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
		};
	},
	computed: {
		...mapGetters({
			loading: [GridFieldsSettingsGetters.GRID_LOADING],
			entities: [GridsGetters.GET_ENTITIES],
			items: [GridFieldsSettingsGetters.GET_SELECTED_GRIDVIEW_FIELDS],
		}),
		reorderData() {
			return JSON.parse(
				JSON.stringify(this.items.filter((x) => x.IsVisible))
			);
		},
		canView() {
			return this.hasPermissions(Permissions.Settings.GridFields.View);
		},
		canAdd() {
			return this.hasPermissions(Permissions.Settings.GridFields.Add);
		},
		canEdit() {
			return this.hasPermissions(Permissions.Settings.GridFields.Edit);
		},
		canDelete() {
			return this.hasPermissions(Permissions.Settings.GridFields.Delete);
		},
	},
	destroyed() {
		this.$store.dispatch(GridFieldsSettingsActions.CLEAR);
	},
	methods: {
		reorder(props) {
			this.reordered = props;
			this.reordered.forEach((element) => {
				element.Order = this.reordered.indexOf(element) + 1;
			});
			this.$store.dispatch(
				GridFieldsSettingsActions.UPDATE_COLUMNS,
				this.reordered
			);
		},
		onTabItemChanged(e) {
			this.selectedEntity = this.entities[e];
			this.$store.dispatch(
				GridFieldsSettingsActions.LOAD_SELECTED_ENTITY,
				this.selectedEntity
			);
			this.$store.dispatch(
				GridsActions.SET_SELECTED_ENTITY_ID,
				this.selectedEntity.Id
			);
		},
		addField() {
			this.$store.dispatch(GridFieldsSettingsActions.SET_EDIT_FIELD, {});
			this.showGridFieldsModal = true;
		},
		onCloseGridFieldsModal() {
			this.showGridFieldsModal = false;
		},
		onEditGridViewField(item) {
			this.$store.dispatch(
				GridFieldsSettingsActions.SET_EDIT_FIELD,
				item
			);
			this.showGridFieldsModal = true;
		},
		onDeleteGridFields(item) {
			this.$store
				.dispatch(GridFieldsSettingsActions.CHECK_FIELD, item) // Check if gvf is LinkField
				.then((resolve) => {
					if (resolve) {
						ConfirmDialog({
							message:
								'This field cannot be deleted because there are other fields using it as a linkfield',
							yesBtnText: 'Ok',
							showNoBtn: false,
						});
					} else {
						ConfirmDialog({
							message:
								'Are you sure you want to delete this Grid Field?',
						}).then((isDelete) => {
							if (isDelete) {
								this.$store.dispatch(
									GridFieldsSettingsActions.DELETE_FIELD,
									item.Id
								);
							}
						});
					}
				});
		},
		onSubmitGridFieldsModal() {
			this.showReorderModal = false;
			if (this.selectedEntity) {
				this.$store.dispatch(
					GridFieldsSettingsActions.LOAD_SELECTED_ENTITY,
					this.selectedEntity
				);
			}
		},
	},
};
</script>

<style lang="scss">
.v-tabs-bar {
	height: 41px;
}
</style>
